import React, { useMemo, useState } from 'react';
import './ExercisePanel.scss';

import { useOrganizationContext } from '../../context/OrganizationContext';
import { useForm } from '../../hooks/useForm';
import { FormField } from '../FormField/FormField';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Image } from 'primereact/image';
import { RequestFeedbackMessages, useClient } from '../../hooks/useClient';
import { Exercise, EXERCISE_TYPE } from '../../types/Fisiofit/exercise';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { resourceMutator } from '../../utils/resourceMutation';
import { mutate } from 'swr';
import { EXERCISE_CATEGORIES, MUSCLE_GROUPS } from '../../types/Fisiofit/fisiofitConst';
import { Panel, ResourcePanelImplProps } from '../Panel/Panel';
import { EXERCISE_FORM_VALIDATORS, ExerciseForm } from './validators';
import { EncodedImage } from '../../types/filedata';
import { FileUploadButton } from '../FileUploadButton/FileUploadButton';
import { capitalize } from '../../utils/textUtils';
import { getExerciseDeleteConfirmationMessage } from '../../pages/Exercises/Exercises';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';
import { getVideoEmbedUrl, isEmbeddableVideo } from '../../utils/videoEmbedUtils';

const getToastMessages = (isEditing: boolean): RequestFeedbackMessages => {
  return {
    successMessage: {
      summary: isEditing ? 'Ejercicio modificado' : 'Ejercicio creado',
      detail: isEditing ? 'El ejercicio se ha modificado correctamente' : 'Se ha creado el nuevo ejercicio',
    },
    errorMessages: {
      summary: isEditing ? 'Error modificando el ejercicio' : 'Error al crear el ejercicio',
      defaultDetail: isEditing ? 'No se ha podido modificar el ejercicio' : 'No se ha podido crear el ejercicio',
      RESOURCE_WITH_SAME_NAME_EXISTS: 'Ya existe un ejercicio con ese nombre',
    },
  };
};

const EXERCISE_TYPE_OPTIONS = [
  { label: 'Repeticiones', value: EXERCISE_TYPE.REPS },
  { label: 'Tiempo', value: EXERCISE_TYPE.TIME },
];

const MUSCLE_GROUP_OPTIONS = Object.values(MUSCLE_GROUPS).map((value) => ({
  label: capitalize(value),
  value: value,
}));

const CATEGORY_OPTIONS = Object.values(EXERCISE_CATEGORIES).map((value) => ({
  label: capitalize(value),
  value: value,
}));

const getInitialExerciseForm = (exercise?: Exercise): ExerciseForm => {
  const { muscleGroups, categories, name, description, videoUrl, imageUrl, exerciseType } = exercise ?? {};
  return {
    name,
    muscleGroups: new Set(muscleGroups),
    categories: categories && categories.length > 0 ? new Set(categories) : new Set(),
    description,
    video: videoUrl,
    image: imageUrl,
    exerciseType: exerciseType ?? EXERCISE_TYPE.REPS,
  };
};

export const ExercisePanel = ({
  resource,
  visible,
  onHide,
  onIsEditingChange,
  onDeleteResource,
  isEditing = false,
  isDeleting = false,
  allowedFeatures,
}: ResourcePanelImplProps<Exercise>) => {
  const { organization } = useOrganizationContext();
  const { post, patch } = useClient();
  const [imageFile, setImageFile] = useState<File>();

  const isCreating = !resource;

  const initialForm = useMemo(() => getInitialExerciseForm(resource), [resource]);
  const { form, setFormFields, isSaving, setIsSaving, hasChanged, validationErrors, resetForm, validate } = useForm<ExerciseForm>(
    initialForm,
    !visible,
    EXERCISE_FORM_VALIDATORS,
  );

  const handleImageUpload = (encodedImage: EncodedImage, file: File) => {
    setImageFile(file);
    setFormFields({
      image: encodedImage,
    });
  };

  const handleSave = async (): Promise<boolean> => {
    const errors = validate();
    const { name, description, muscleGroups, categories, image: _, video, exerciseType } = form ?? {};
    if (!name || !exerciseType || !organization || errors) return false;
    setIsSaving(true);

    const formData = new FormData();
    const exerciseData = {
      name,
      description,
      muscleGroups: Array.from(muscleGroups ?? []),
      categories: Array.from(categories ?? []),
      videoUrl: video,
      exerciseType,
      ...(isCreating && { organizationId: organization.id }),
    };

    formData.append('data', JSON.stringify(exerciseData));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const { successMessage, errorMessages } = getToastMessages(isEditing);
    const request = {
      body: formData,
      successMessage,
      errorMessages,
    };

    const response =
      isEditing && resource
        ? await patch<Exercise>(`/exercises/${resource.id}`, request)
        : await post<Exercise>(`/exercises`, request);

    if (response) {
      mutate(`/organizations/${organization.id}/exercises`, resourceMutator<Exercise>(response), false);
      const tableKey = `/organizations/${organization.id}/exercises/paginated?`;
      mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
    }

    setIsSaving(false);
    return !!response;
  };

  return (
    <Panel
      panelType='resourcePanel'
      resourceName='ejercicio'
      visible={visible}
      onHide={onHide}
      onDelete={() => onDeleteResource(resource?.id)}
      isDeleting={isDeleting}
      onSave={handleSave}
      onIsEditingChange={onIsEditingChange}
      hasChanged={hasChanged}
      isSaving={isSaving}
      isEditing={isEditing}
      isCreating={isCreating}
      resetForm={resetForm}
      allowedFeatures={allowedFeatures}
      customDeleteConfirmationDialogProps={{
        message: getExerciseDeleteConfirmationMessage(resource),
        header: 'Eliminar ejercicio',
      }}
    >
      <form
        className={classNames({
          'form-loading': isSaving,
        })}
      >
        <FormField
          labelTitle='Nombre'
          elementId='exercise-name'
          fullWidth
          error={validationErrors?.name}
          required={isCreating || isEditing}
        >
          {isEditing || isCreating ? (
            <InputText
              id='exercise-name'
              className={classNames({
                'p-invalid': validationErrors?.name,
              })}
              placeholder='Ej: Press de banca'
              value={form?.name ?? ''}
              onChange={(e: any) =>
                setFormFields({
                  name: e.target.value,
                })
              }
            />
          ) : (
            <p>{form?.name}</p>
          )}
        </FormField>

        <FormField
          labelTitle='Tipo de ejercicio'
          elementId='exercise-type'
          fullWidth
          error={validationErrors?.exerciseType}
          required={isCreating || isEditing}
        >
          {isCreating ? (
            <Dropdown
              id='exercise-type'
              className={classNames({
                'p-invalid': validationErrors?.exerciseType,
              })}
              value={form?.exerciseType}
              options={EXERCISE_TYPE_OPTIONS}
              onChange={(e) =>
                setFormFields({
                  exerciseType: e.value,
                })
              }
              placeholder='Selecciona el tipo de ejercicio'
            />
          ) : (
            <p>{form?.exerciseType === EXERCISE_TYPE.REPS ? 'Repeticiones' : 'Tiempo'}</p>
          )}
        </FormField>

        <FormField
          labelTitle='Descripción'
          elementId='exercise-description'
          fullWidth
          error={validationErrors?.description}
          required={isCreating || isEditing}
        >
          {isEditing || isCreating ? (
            <InputTextarea
              id='exercise-description'
              className={classNames({
                'p-invalid': validationErrors?.description,
              })}
              placeholder={`Escribe la descripción del ejercicio`}
              autoResize
              value={form?.description ?? ''}
              onChange={(e: any) =>
                setFormFields({
                  description: e.target.value,
                })
              }
              rows={3}
            />
          ) : (
            <p className='description-text'>{form?.description}</p>
          )}
        </FormField>

        <FormField
          labelTitle='Músculos implicados'
          elementId='exercise-muscles'
          fullWidth
          error={validationErrors?.muscleGroups}
          required={isCreating || isEditing}
        >
          {isEditing || isCreating ? (
            <AdaptiveSelect
              isMultiple
              value={Array.from(form?.muscleGroups ?? [])}
              className={classNames({
                'p-invalid': validationErrors?.muscleGroups,
              })}
              options={MUSCLE_GROUP_OPTIONS}
              onChange={(e) =>
                setFormFields({
                  muscleGroups: new Set(e),
                })
              }
              itemTemplate={(option) => <span>{option.label}</span>}
              placeholder='Selecciona músculos'
              modalTitle='Seleccionar músculos'
              searchPlaceholder='Buscar músculos'
              emptyMessage='No hay músculos'
              emptyFilterMessage='No se han encontrado músculos'
            />
          ) : (
            <p>
              {Array.from(form?.muscleGroups ?? [])
                .map(capitalize)
                .join(', ')}
            </p>
          )}
        </FormField>

        <FormField labelTitle='Categoría' elementId='exercise-categories' fullWidth error={validationErrors?.categories} required>
          {isEditing || isCreating ? (
            <AdaptiveSelect
              value={Array.from(form?.categories ?? []).length > 0 ? Array.from(form?.categories ?? [])[0] : undefined}
              className={classNames({
                'p-invalid': validationErrors?.categories,
              })}
              options={CATEGORY_OPTIONS}
              onChange={(e) =>
                setFormFields({
                  categories: e ? new Set([e]) : new Set(),
                })
              }
              itemTemplate={(option) => <span>{option.label}</span>}
              placeholder='Selecciona una categoría'
              modalTitle='Seleccionar categoría'
              searchPlaceholder='Buscar categoría'
              emptyMessage='No hay categoría'
              emptyFilterMessage='No se ha encontrado categoría'
            />
          ) : (
            <p>
              {Array.from(form?.categories ?? [])
                .map(capitalize)
                .join(', ')}
            </p>
          )}
        </FormField>

        <FormField labelTitle='URL del vídeo' elementId='video' error={validationErrors?.video} fullWidth>
          {(isEditing || isCreating) && (
            <InputText
              id='video'
              value={form?.video ?? ''}
              onChange={(e) => setFormFields({ video: e.target.value })}
              placeholder='Introduce la URL del vídeo (YouTube, Drive, etc.)'
              className={classNames({
                'p-invalid': validationErrors?.video,
              })}
            />
          )}
          {form?.video || isEditing || isCreating ? (
            <div
              className={classNames('preview-container', {
                placeholder: !form?.video,
              })}
            >
              {form?.video ? (
                isEmbeddableVideo(form.video) ? (
                  <iframe
                    className='preview-video'
                    src={getVideoEmbedUrl(form.video)}
                    title='Video del ejercicio'
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    sandbox='allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox'
                  />
                ) : (
                  <video className='preview-video' controls>
                    <source src={form.video} type='video/mp4' />
                    <source src={form.video} type='video/webm' />
                    <source src={form.video} type='video/ogg' />
                    Tu navegador no soporta el elemento de vídeo.
                  </video>
                )
              ) : (
                <p>No hay vídeo asociado</p>
              )}
            </div>
          ) : (
            <p>No hay vídeo asociado</p>
          )}
        </FormField>

        <FormField labelTitle='Imagen' elementId='image' error={validationErrors?.image} fullWidth>
          {(isEditing || isCreating) && (
            <FileUploadButton
              label='Subir imagen'
              fileName={`${form?.name}_image.png`}
              accept='image/*'
              onFileUpload={handleImageUpload}
            />
          )}
          {form?.image || isEditing || isCreating ? (
            <div
              className={classNames('preview-container', {
                placeholder: !form?.image,
              })}
            >
              {form?.image ? (
                <Image className='preview-image' src={form.image} preview={true} alt='Vista previa del ejercicio' />
              ) : (
                <p>No hay imagen asociada</p>
              )}
            </div>
          ) : (
            <p>No hay imagen asociada</p>
          )}
        </FormField>
      </form>
    </Panel>
  );
};
